import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { CircularProgress, Box, Typography, Paper } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';

interface PermissionProtectedRouteProps {
  children: React.ReactNode;
  sectionId: string;
  pageId: string;
  actionId?: string; // اختياري لأن بعض الصفحات قد تتطلب الوصول فقط بدون إجراء محدد
}

const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({
  children,
  sectionId,
  pageId,
  actionId = 'view_page' // افتراضيًا نتحقق من صلاحية مشاهدة الصفحة
}) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { hasPermission, loading: permissionsLoading, refreshPermissions } = usePermissions();
  const [lastCheckedPermission, setLastCheckedPermission] = useState<boolean | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  
  // التحقق من الصلاحيات بمجرد تحميل البيانات
  useEffect(() => {
    // لا نتحقق من الصلاحيات إلا بعد اكتمال تحميل الصلاحيات وتأكيد تسجيل الدخول
    if (!authLoading && !permissionsLoading && isAuthenticated) {
      console.log(`🔍 PermissionProtectedRoute - التحقق من الصلاحية: ${sectionId}/${pageId}/${actionId}`);
      console.log(`🔍 PermissionProtectedRoute - حالة التحميل: authLoading=${authLoading}, permissionsLoading=${permissionsLoading}, isAuthenticated=${isAuthenticated}`);
      
      const access = hasPermission(sectionId, pageId, actionId);
      setHasAccess(access);
      console.log(`🔍 PermissionProtectedRoute - نتيجة الصلاحية ${sectionId}/${pageId}/${actionId}: ${access}`);
      
      // تخزين نتيجة الفحص إذا كانت هذه أول مرة نتحقق فيها
      if (lastCheckedPermission === null) {
        setLastCheckedPermission(access);
      }
    } else {
      console.log(`🔍 PermissionProtectedRoute - لم يتم التحقق من الصلاحيات بعد: authLoading=${authLoading}, permissionsLoading=${permissionsLoading}, isAuthenticated=${isAuthenticated}`);
    }
  }, [authLoading, permissionsLoading, isAuthenticated, hasPermission, sectionId, pageId, actionId, lastCheckedPermission]);
  
  // إعادة محاولة التحقق من الصلاحيات عند الحاجة
  useEffect(() => {
    // إعادة محاولة التحقق فقط إذا كانت النتيجة الأولى سلبية وما زلنا ضمن عدد المحاولات المسموح
    if (lastCheckedPermission === false && retryCount < 2) {
      console.log(`إعادة محاولة التحقق من الصلاحيات (${retryCount + 1}/2)...`);
      const timer = setTimeout(() => {
        refreshPermissions().then(() => {
          setRetryCount(prev => prev + 1);
        });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [lastCheckedPermission, retryCount, refreshPermissions]);
  
  // حالات العرض المختلفة
  
  // حالة التحميل
  if (authLoading || permissionsLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          جاري التحقق من الصلاحيات...
        </Typography>
      </Box>
    );
  }
  
  // إذا لم يكن المستخدم مصادق عليه، نوجهه إلى صفحة تسجيل الدخول
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // إذا لم يكن لديه صلاحية الوصول، نعرض رسالة خطأ
  if (hasAccess === false) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '80vh',
        px: 3
      }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: 500
          }}
        >
          <LockOutlined color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom textAlign="center">
            غير مصرح بالوصول
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            ليس لديك صلاحيات كافية للوصول إلى هذه الصفحة.
            <br />
            يرجى التواصل مع مسؤول النظام للحصول على الصلاحيات المطلوبة.
          </Typography>
        </Paper>
      </Box>
    );
  }
  
  // إذا كان لديه صلاحية الوصول، نعرض المحتوى
  return <>{children}</>;
};

export default PermissionProtectedRoute;